import { number } from "prop-types";
import { createContext, useContext, useEffect, useState } from "react";

import styled from "styled-components/macro";

import { useFlocknoteAppState } from "Flocknote/state";
import { useHasGivingTab } from "Flocknote/stateHooks";

import { useGetHouseholdDetail } from "HouseholdProfile/API/queries";
import { useInvalidateActiveHouseholdDetailViaLegacy } from "HouseholdProfile/API/queryHooks";

import FnrDivider from "UI/FnrDivider";
import FnrLoadingBricks2 from "UI/FnrLoadingBricks2";
import FnrVerticalGap from "UI/commonContainers/FnrVerticalGap";
import { scrollbarLightThin } from "UI/scrollbarStyles";

import useIsMobile from "Utilities/Hooks/useIsMobile";
import useResizeEvent from "Utilities/Hooks/useResizeEvent";
import useUrlParams from "Utilities/Hooks/useUrlParams";

import FamilyTab from "../FamilyTab";
import GoToGivingTab from "../GoToGivingTab";
import GoToOrCreateHouseHold from "../GoToOrCreateHouseHold";
import { useShouldShowFamilyInfoInSidebar } from "../GoToOrCreateHouseHold/useShouldShowFamilyInfoInSidebar";
import HouseholdMembers from "../HouseholdMembers";

const ProfileSidebarContext = createContext();
const useProfileSidebarContext = () => useContext(ProfileSidebarContext) || {};

const Wrapper = styled(FnrVerticalGap)`
  height: 100%;
  overflow-x: auto;
  overflow-y: visible;
  ${scrollbarLightThin}
  scrollbar-width: none;
  scrollbar-color: #9fa2a5 #ededec;
`;

const SFnrVerticalGap = styled(FnrVerticalGap)`
  margin: 0px 8px;

  @media screen and (max-width: 680px) {
    flex-direction: row;
    margin: ${({ $isProfilePage }) =>
      $isProfilePage ? "0 8px" : "0 8px 0 49px"};
    gap: 20px;
  }
`;
const SFnrDivider = styled(FnrDivider)`
  width: calc(100% - 4px);
  margin-inline: 2px;
  margin-block: 0.625rem 1rem;

  @media screen and (max-width: 680px) {
    display: none;
  }
`;

const ProfileSidebarContents = ({ householdID }) => {
  const { isLoading } = useGetHouseholdDetail();
  const { showHousehold, setShowHousehold } = useProfileSidebarContext();
  // TODO: move dispaly logic here for clarity. It is currently down in the components.
  const showFamilyInfo = useShouldShowFamilyInfoInSidebar();
  const showGiving = useHasGivingTab();
  const { profileTab } = useUrlParams();
  const [hasBeenMounted, setHasBeenMounted] = useState(false);
  const showDivider = showGiving || showFamilyInfo;
  const isMobile = useIsMobile(680);
  const [{ navigationProfileTabName }] = useFlocknoteAppState();

  // https://flocknote.monday.com/boards/7401187643/pulses/7630065491
  useInvalidateActiveHouseholdDetailViaLegacy();
  useResizeEvent(() => {
    if (!isMobile) setShowHousehold(false);
  });

  // initialize the tab from the url. Browser storage for the tab history does not know what's in the URL on init.
  useEffect(() => {
    if (
      !hasBeenMounted &&
      profileTab &&
      navigationProfileTabName !== profileTab // prevents calling when the tab is already being navigated to from elsewhere in the system
    )
      // Push this call to the bottom of the stack so app init can finish and build the bus first.
      setTimeout(() => {
        window.fnReactBus.navigateToProfileTab(profileTab);
      });
    setHasBeenMounted(true);
  }, [profileTab, hasBeenMounted, navigationProfileTabName]);

  const show = isMobile ? showHousehold : true;
  const isProfilePage = Boolean(
    document.querySelector(".profile-page-wrapper")
  );

  return (
    <Margin $householdShowing={showHousehold}>
      <Wrapper gap={0}>
        <SFnrVerticalGap gap={6} $isProfilePage={isProfilePage}>
          <FamilyTab />
          <GoToOrCreateHouseHold />
          <GoToGivingTab />
          {showDivider && <SFnrDivider color="faintTaupe" thickness={2} />}
        </SFnrVerticalGap>
        <FnrLoadingBricks2
          loading={isLoading}
          color="brandFnBlue"
          size={28}
          $yAdjust={14}
        />
        {show && (
          <HouseholdMembers key={householdID} householdID={householdID} />
        )}
      </Wrapper>
    </Margin>
  );
};

ProfileSidebarContents.propTypes = { householdID: number };
ProfileSidebar.propTypes = { householdID: number };

export default function ProfileSidebar({ householdID }) {
  const width = document.querySelector("body").clientWidth;
  const [showHousehold, setShowHousehold] = useState(width > 680);

  return (
    <ProfileSidebarContext.Provider
      value={{ householdID, showHousehold, setShowHousehold }}
    >
      <ProfileSidebarContents householdID={householdID} />
    </ProfileSidebarContext.Provider>
  );
}

export { useProfileSidebarContext };

const Margin = styled.div`
  margin-inline: 4px;
  padding-top: 80px;
  height: 100%;

  @media (max-width: 680px) {
    margin-inline: 10px;
    padding-block-start: 4px;
    padding-block-end: ${({ $householdShowing }) =>
      $householdShowing ? "0.75rem" : "0.25rem"};
    transition: padding-block-end 300ms;
  }
  @media screen and (max-width: 440px) {
    padding-block-start: 9px;
  }
`;